import React from "react"
import type { CSSProperties, ReactNode } from "react"
import cx from "classnames"

const DEFAULT = "rounded-full inline-flex font-semibold"
const SIZES = {
  sm: "px-[0.375rem] text-xs leading-5",
  lg: "px-2 text-sm leading-6"
}
const COLORS = {
  black: {
    solid: "bg-gray-900 text-white",
    outline: "bg-transparent ring-1 ring-gray-900 text-gray-900",
    light: "bg-gray-900 text-white"
  },
  blue: {
    solid: "bg-blue-500 text-white",
    outline: "bg-transparent ring-1 ring-blue-600 text-blue-600",
    light: "bg-blue-100 text-blue-800"
  },
  gray: {
    solid: "bg-gray-800 text-white",
    outline: "bg-transparent ring-1 ring-gray-800 text-gray-800",
    light: "bg-gray-100 text-gray-900"
  },
  green: {
    solid: "bg-green-500 text-white",
    outline: "bg-transparent ring-1 ring-green-600 text-green-600",
    light: "bg-green-100 text-green-800"
  },
  orange: {
    solid: "bg-orange-500 text-white",
    outline: "bg-transparent ring-1 ring-orange-600 text-orange-600",
    light: "bg-orange-100 text-orange-800"
  },
  purple: {
    solid: "bg-purple-500 text-white",
    outline: "bg-transparent ring-1 ring-purple-600 text-purple-600",
    light: "bg-purple-100 text-purple-800"
  },
  red: {
    solid: "bg-red-500 text-white",
    outline: "bg-transparent ring-1 ring-red-600 text-red-600",
    light: "bg-red-100 text-red-800"
  },
  white: {
    solid: "bg-white text-gray-900",
    outline: "bg-transparent ring-1 ring-white text-white",
    light: "bg-white text-gray-900"
  }
}

export type BadgeProps = {
  /** Content displayed inside the badge */
  children: ReactNode
  /** @deprecated Use the `color` and `variant` props instead. */
  className?: string
  /** Color theme of the badge */
  color?:
    | "black"
    | "blue"
    | "gray"
    | "green"
    | "orange"
    | "purple"
    | "red"
    | "white"
  /** Visual style variant */
  variant?: "solid" | "outline" | "light"
  /** Size of the badge text */
  size?: "sm" | "lg"
  /** @deprecated Avoid inline styles; prefer design system props. */
  style?: CSSProperties
  /** Accessible label for the badge */
  "aria-label"?: string
  /** Whether to hide the badge from assistive technology */
  "aria-hidden"?: boolean
}

/**
 * A small label for categorizing or highlighting status.
 * Supports multiple colors, variants, and sizes.
 */
const Badge = ({
  children,
  className: classNameProp,
  color = "gray",
  variant = "solid",
  size = "sm",
  style,
  "aria-label": ariaLabel,
  "aria-hidden": ariaHidden
}: BadgeProps) => {
  const className = cx(
    classNameProp,
    DEFAULT,
    COLORS[color][variant],
    SIZES[size]
  )

  return (
    <span
      className={className}
      style={style}
      aria-label={ariaLabel}
      aria-hidden={ariaHidden}
    >
      {children}
    </span>
  )
}

export default Badge
export { Badge, COLORS }
